import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const C = {
  red: '#E8192C',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  bg: '#0C0905',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textMuted: '#5C554E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, sans-serif',
};

export const SETTINGS_STORAGE_KEY = 'repeaked:settings:v1';

export const AUDIO_OPTIONS = [
  {
    id: 'default',
    label: 'Default',
    description: 'A sharp Repeaked queue hit for match-ready moments.',
    src: '/assets/audio/match-found-default.mp3',
  },
  {
    id: 'alert',
    label: 'Alert',
    description: 'A louder signal for when you are tabbed out or distracted.',
    src: '/assets/audio/match-found-alert.mp3',
  },
  {
    id: 'off',
    label: 'Off',
    description: 'Disable match-found audio and rely on visual prompts only.',
    src: null,
  },
];

const DEFAULT_SETTINGS = {
  matchFoundAudio: 'default',
};

const VALID_AUDIO_IDS = new Set(AUDIO_OPTIONS.map((option) => option.id));

export function getSteamTrustDisplay(user) {
  if (!user?.steam_id) {
    return {
      label: 'Not Linked',
      tone: 'muted',
      description: 'Link Steam to unlock trusted matchmaking checks once they are enforced.',
    };
  }

  switch (user.steam_trust_status) {
    case 'trusted':
      return {
        label: 'Trusted',
        tone: 'good',
        description: 'Steam account verified and trusted for future matchmaking checks.',
      };
    case 'blocked':
      return {
        label: 'Blocked',
        tone: 'danger',
        description: 'Steam account linked, but flagged by anti-smurf checks for admin verification.',
      };
    case 'suspicious':
      return {
        label: 'Pending Review',
        tone: 'warn',
        description: 'Steam account linked. Repeaked is holding this trust status for admin verification.',
      };
    case 'unchecked':
      return {
        label: 'Linked - Not Checked',
        tone: 'neutral',
        description: 'Steam account linked. Trust checks will run the next time Steam verification refreshes.',
      };
    default:
      return {
        label: 'Linked - Not Checked',
        tone: 'neutral',
        description: 'Steam account linked. Trust checks have not been recorded for this account yet.',
      };
  }
}

export function readSettings() {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;

  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;

    const parsed = JSON.parse(raw);
    const matchFoundAudio = VALID_AUDIO_IDS.has(parsed?.matchFoundAudio)
      ? parsed.matchFoundAudio
      : DEFAULT_SETTINGS.matchFoundAudio;

    return { ...DEFAULT_SETTINGS, matchFoundAudio };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(nextSettings) {
  window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
}

function Eyebrow({ children }) {
  return <div className="rs-eyebrow"><span className="rs-diamond" />{children}</div>;
}

function Pill({ tone = 'neutral', children }) {
  return <span className={`rs-pill rs-pill-${tone}`}>{children}</span>;
}

function Card({ eyebrow, title, action, children, className = '' }) {
  return (
    <section className={`rs-card ${className}`}>
      <div className="rs-card-head">
        <div>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2>{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SettingRow({ title, description, status, action }) {
  return (
    <div className="rs-setting-row">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="rs-setting-action">
        {status}
        {action}
      </div>
    </div>
  );
}

function SettingsStyles() {
  return (
    <style>{`
      @keyframes rsUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .rs-page { height: 100vh; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; background: ${C.bg}; color: ${C.text}; font-family: ${F.body}; padding: 24px; animation: rsUp 360ms ease both; box-sizing: border-box; }
      .rs-shell { max-width: 1400px; margin: 0 auto; display: grid; gap: 18px; }
      .rs-hero { min-height: 220px; border: 1px solid ${C.border}; background: radial-gradient(circle at 16% 18%, rgba(232,25,44,.28), transparent 28%), linear-gradient(135deg, #130705 0%, #0c0905 54%, #050505 100%); position: relative; overflow: hidden; padding: 28px; display: grid; align-items: end; }
      .rs-hero::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(0,0,0,.38), rgba(0,0,0,.08)); pointer-events: none; }
      .rs-hero-copy { position: relative; z-index: 1; max-width: 820px; }
      .rs-hero h1 { font-family: ${F.display}; font-size: clamp(52px, 7vw, 92px); letter-spacing: 8px; line-height: .88; margin: 8px 0 12px; font-weight: 400; text-shadow: 0 14px 34px rgba(0,0,0,.58); }
      .rs-hero h1 span { color: ${C.red}; }
      .rs-hero p { color: rgba(240,242,245,.72); font-size: 16px; line-height: 1.5; max-width: 680px; margin: 0; letter-spacing: .5px; }
      .rs-grid { display: grid; grid-template-columns: minmax(300px, .86fr) minmax(420px, 1.14fr); gap: 18px; align-items: start; }
      .rs-stack { display: grid; gap: 18px; }
      .rs-card { background: ${C.card}; border-top: 1px solid ${C.border}; border-right: 1px solid ${C.border}; border-bottom: 1px solid ${C.border}; border-left: 2px solid ${C.red}; padding: 22px; min-width: 0; box-sizing: border-box; }
      .rs-card-alt { border-left-color: ${C.orange}; }
      .rs-card-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 18px; }
      .rs-card h2 { font-family: ${F.display}; font-size: 30px; letter-spacing: 4px; margin: 6px 0 0; line-height: 1; font-weight: 400; }
      .rs-eyebrow { display: flex; align-items: center; gap: 10px; color: ${C.textDim}; font-size: 10px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; }
      .rs-diamond { width: 7px; height: 7px; display: inline-block; background: ${C.red}; transform: rotate(45deg); }
      .rs-profile-card { display: grid; gap: 8px; background: ${C.cardAlt}; border: 1px solid ${C.border}; padding: 16px; }
      .rs-profile-card strong { font-family: ${F.display}; font-size: 28px; letter-spacing: 3px; line-height: 1; }
      .rs-profile-card span { color: ${C.textMuted}; letter-spacing: 1.5px; text-transform: uppercase; font-size: 11px; font-weight: 800; }
      .rs-setting-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 14px; align-items: center; padding: 15px 0; border-top: 1px solid ${C.border}; }
      .rs-setting-row:first-child { border-top: none; padding-top: 0; }
      .rs-setting-row:last-child { padding-bottom: 0; }
      .rs-setting-row h3 { margin: 0 0 5px; font-family: ${F.display}; font-size: 22px; letter-spacing: 2px; font-weight: 400; }
      .rs-setting-row p, .rs-copy { margin: 0; color: ${C.textDim}; line-height: 1.45; font-size: 14px; letter-spacing: .4px; }
      .rs-setting-action { display: flex; gap: 10px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }
      .rs-pill { display: inline-flex; align-items: center; min-height: 28px; padding: 0 10px; border: 1px solid ${C.borderBright}; background: rgba(255,255,255,.03); color: ${C.textDim}; font-size: 10px; font-weight: 800; letter-spacing: 1.8px; text-transform: uppercase; white-space: nowrap; }
      .rs-pill-good { border-color: rgba(63,203,126,.34); background: rgba(63,203,126,.08); color: ${C.green}; }
      .rs-pill-warn { border-color: rgba(255,107,43,.38); background: rgba(255,107,43,.09); color: ${C.orange}; }
      .rs-pill-danger { border-color: rgba(232,25,44,.48); background: rgba(232,25,44,.1); color: ${C.red}; }
      .rs-pill-muted { color: ${C.textMuted}; }
      .rs-button { border: 1px solid ${C.borderBright}; background: #080808; color: ${C.text}; min-height: 38px; padding: 0 14px; font-family: ${F.body}; font-size: 11px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; }
      .rs-button-primary { border-color: rgba(232,25,44,.55); background: linear-gradient(135deg, ${C.red}, ${C.orange}); color: #fff; }
      .rs-button:disabled { opacity: .45; cursor: not-allowed; transform: none; filter: none; }
      .rs-audio-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 18px 0; }
      .rs-audio-option { position: relative; display: grid; gap: 8px; min-height: 138px; padding: 16px; border: 1px solid ${C.border}; background: ${C.cardAlt}; color: ${C.text}; text-align: left; cursor: pointer; }
      .rs-audio-option.is-selected { border-color: rgba(232,25,44,.72); box-shadow: inset 3px 0 0 ${C.red}; background: linear-gradient(180deg, rgba(232,25,44,.12), rgba(22,17,13,.98)); }
      .rs-audio-option input { position: absolute; opacity: 0; pointer-events: none; }
      .rs-audio-option strong { font-family: ${F.display}; font-size: 26px; letter-spacing: 3px; font-weight: 400; }
      .rs-audio-option p { margin: 0; color: ${C.textDim}; font-size: 13px; line-height: 1.38; }
      .rs-audio-option small { align-self: end; color: ${C.orange}; font-weight: 800; letter-spacing: 1.7px; text-transform: uppercase; }
      .rs-audio-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
      .rs-status { border: 1px solid ${C.border}; background: rgba(255,255,255,.03); color: ${C.textDim}; padding: 10px 12px; line-height: 1.35; letter-spacing: .8px; }
      .rs-status.is-error { border-color: rgba(232,25,44,.42); background: rgba(232,25,44,.08); color: ${C.text}; }
      .rs-mini-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
      .rs-mini-card { border: 1px solid ${C.border}; background: ${C.cardAlt}; padding: 16px; min-height: 124px; }
      .rs-mini-card h3 { font-family: ${F.display}; font-size: 23px; letter-spacing: 2px; font-weight: 400; margin: 10px 0 8px; }
      .rs-mini-card p { color: ${C.textDim}; line-height: 1.4; margin: 0; font-size: 13px; }
      @media (max-width: 980px) {
        .rs-grid { grid-template-columns: 1fr; }
        .rs-audio-options, .rs-mini-grid { grid-template-columns: 1fr; }
      }
      @media (max-width: 640px) {
        .rs-page { padding: 16px; }
        .rs-hero { min-height: 190px; padding: 20px; }
        .rs-setting-row { grid-template-columns: 1fr; }
        .rs-setting-action { justify-content: flex-start; }
        .rs-card-head { flex-direction: column; }
      }
      @media (prefers-reduced-motion: reduce) {
        .rs-page { animation: none; }
      }
    `}</style>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(readSettings);
  const [audioStatus, setAudioStatus] = useState('');
  const [audioError, setAudioError] = useState('');
  const [securityNotice, setSecurityNotice] = useState('');

  const selectedAudio = useMemo(
    () => AUDIO_OPTIONS.find((option) => option.id === settings.matchFoundAudio) || AUDIO_OPTIONS[0],
    [settings.matchFoundAudio]
  );
  const steamTrust = getSteamTrustDisplay(user);

  const username = user?.username || 'Player';
  const accountId = user?.id || user?.user_id || 'Current session';

  const updateAudio = (audioId) => {
    const nextSettings = { ...settings, matchFoundAudio: audioId };
    setSettings(nextSettings);
    saveSettings(nextSettings);
    setAudioError('');
    setAudioStatus(`${AUDIO_OPTIONS.find((option) => option.id === audioId)?.label || 'Default'} saved for match-found alerts.`);
  };

  const previewAudio = async () => {
    setAudioStatus('');
    setAudioError('');

    if (!selectedAudio.src) {
      setAudioStatus('Match-found audio is turned off.');
      return;
    }

    try {
      const audio = new Audio(selectedAudio.src);
      audio.volume = 0.75;
      await audio.play();
      setAudioStatus(`Previewing ${selectedAudio.label}.`);
    } catch {
      setAudioError('Could not preview that sound. The file may not exist yet or the browser blocked playback.');
    }
  };

  return (
    <div className="rs-page">
      <SettingsStyles />
      <div className="rs-shell">
        <header className="rs-hero">
          <div className="rs-hero-copy">
            <Eyebrow>Account Command Center</Eyebrow>
            <h1>Settings<span>.</span></h1>
            <p>Control account security, match preferences, and the platform defaults that will matter once matchmaking goes live.</p>
          </div>
        </header>

        <main className="rs-grid">
          <div className="rs-stack">
            <Card eyebrow="Account" title="Security">
              <div className="rs-profile-card">
                <span>Signed in as</span>
                <strong>{username}</strong>
                <span>{accountId}</span>
              </div>

              <SettingRow
                title="Password Reset"
                description="A proper password reset or change flow should connect to the backend auth system. This page marks the action without pretending it is live."
                status={<Pill tone="warn">Backend Needed</Pill>}
                action={(
                  <button className="rs-button" type="button" onClick={() => setSecurityNotice('Password reset needs a backend endpoint before it can be enabled.')}>
                    Plan Flow
                  </button>
                )}
              />
              <SettingRow
                title="Steam Link"
                description={steamTrust.description}
                status={<Pill tone={steamTrust.tone}>{steamTrust.label}</Pill>}
              />
              <SettingRow
                title="Two-Factor Authentication"
                description="Reserve space for 2FA controls once account security expands."
                status={<Pill tone="muted">Coming Soon</Pill>}
              />
              <SettingRow
                title="Active Sessions"
                description="Future session management can show devices, sign-out controls, and suspicious login warnings."
                status={<Pill tone="muted">Coming Soon</Pill>}
              />
              {securityNotice && <div className="rs-status" role="status">{securityNotice}</div>}
            </Card>

            <Card eyebrow="Social" title="Privacy">
              <div className="rs-mini-grid">
                <div className="rs-mini-card">
                  <Pill tone="muted">Coming Soon</Pill>
                  <h3>Profile Visibility</h3>
                  <p>Move profile, friends, and comments visibility controls here when they graduate from profile editing.</p>
                </div>
                <div className="rs-mini-card">
                  <Pill tone="muted">Coming Soon</Pill>
                  <h3>Invite Rules</h3>
                  <p>Choose who can send party invites, friend requests, and lobby invitations.</p>
                </div>
              </div>
            </Card>
          </div>

          <div className="rs-stack">
            <Card
              eyebrow="Matchmaking"
              title="Match Audio"
              className="rs-card-alt"
              action={<Pill tone="good">Local Save</Pill>}
            >
              <p className="rs-copy">
                Pick the sound Repeaked should use when a match is found. This is stored on this browser for now and can be wired into matchmaking later.
              </p>

              <div className="rs-audio-options" role="radiogroup" aria-label="Match found audio">
                {AUDIO_OPTIONS.map((option) => {
                  const selected = selectedAudio.id === option.id;
                  return (
                    <label key={option.id} className={`rs-audio-option ${selected ? 'is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="match-found-audio"
                        value={option.id}
                        checked={selected}
                        onChange={() => updateAudio(option.id)}
                      />
                      <strong>{option.label}</strong>
                      <p>{option.description}</p>
                      <small>{selected ? 'Selected' : 'Select'}</small>
                    </label>
                  );
                })}
              </div>

              <div className="rs-audio-actions">
                <button className="rs-button rs-button-primary" type="button" onClick={previewAudio} disabled={!selectedAudio.src}>
                  Preview Sound
                </button>
                <Pill>{selectedAudio.src ? selectedAudio.src.replace('/assets/audio/', '') : 'No Sound'}</Pill>
              </div>
              {audioStatus && <div className="rs-status" role="status">{audioStatus}</div>}
              {audioError && <div className="rs-status is-error" role="alert">{audioError}</div>}
            </Card>

            <Card eyebrow="Alerts" title="Notifications">
              <div className="rs-mini-grid">
                <div className="rs-mini-card">
                  <Pill tone="muted">Coming Soon</Pill>
                  <h3>Party Invites</h3>
                  <p>Control invite sounds, desktop alerts, and in-app notification behavior.</p>
                </div>
                <div className="rs-mini-card">
                  <Pill tone="muted">Coming Soon</Pill>
                  <h3>Friend Requests</h3>
                  <p>Choose which social events should interrupt your current screen.</p>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
